import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Copy, Check, Download, FileCode, Braces } from 'lucide-react';
import { CodeBlock } from '@/components/docs/CodeBlock';
import { toast } from 'sonner';
import { SelectionStats, XmlGenerationOptions } from '@/hooks/useAdfBuilder';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface AdfBuilderPreviewProps {
  selectedCount: number;
  stats: SelectionStats;
  generateXml: (options: XmlGenerationOptions) => string;
  onExport: () => void;
}

export function AdfBuilderPreview({
  selectedCount,
  stats,
  generateXml,
  onExport,
}: AdfBuilderPreviewProps) {
  const [copied, setCopied] = useState(false);
  const [includeComments, setIncludeComments] = useState(false);
  const [includeExampleValues, setIncludeExampleValues] = useState(true);
  const [useMergeFields, setUseMergeFields] = useState(false);

  const xmlOutput = useMemo(() => {
    if (selectedCount === 0) return '';
    return generateXml({
      includeComments,
      includeExampleValues: useMergeFields ? false : includeExampleValues,
      useMergeFields,
      indentSize: 2,
    });
  }, [selectedCount, generateXml, includeComments, includeExampleValues, useMergeFields]);

  const fileSize = useMemo(() => {
    if (!xmlOutput) return '0 B';
    const bytes = new TextEncoder().encode(xmlOutput).length;
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  }, [xmlOutput]);

  const handleCopy = async () => {
    if (!xmlOutput) return;
    await navigator.clipboard.writeText(xmlOutput);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!xmlOutput) return;
    const blob = new Blob([xmlOutput], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = useMergeFields ? 'adf-template.xml' : 'adf-subset.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('File downloaded');
  };

  return (
    <div className="flex flex-col h-full border-l">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 p-4 border-b">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="comments"
              checked={includeComments}
              onCheckedChange={setIncludeComments}
              className="scale-75"
            />
            <Label htmlFor="comments" className="text-xs cursor-pointer">
              Comments
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="examples"
              checked={includeExampleValues}
              onCheckedChange={setIncludeExampleValues}
              disabled={useMergeFields}
              className="scale-75"
            />
            <Label htmlFor="examples" className="text-xs cursor-pointer">
              Examples
            </Label>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Switch
                  id="merge-fields"
                  checked={useMergeFields}
                  onCheckedChange={(checked) => {
                    setUseMergeFields(checked);
                    if (checked) setIncludeExampleValues(false);
                  }}
                  className="scale-75"
                />
                <Label htmlFor="merge-fields" className="text-xs cursor-pointer flex items-center gap-1">
                  <Braces className="h-3 w-3" />
                  Template
                </Label>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p>Generate with {"{{field}}"} placeholders for code integration with templating engines like Mustache or Handlebars.</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={!xmlOutput}
            className="gap-1.5"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={!xmlOutput}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Download</span>
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onExport}
            disabled={selectedCount === 0}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </div>
      </div>

      <div className="px-4 py-2 text-sm text-muted-foreground border-b flex items-center justify-between">
        <span>
          {stats.totalSelected} element{stats.totalSelected !== 1 ? 's' : ''} selected
          <span className="hidden sm:inline">
            {' '}({stats.requiredCount} required, {stats.optionalCount} optional)
          </span>
        </span>
        <span>{fileSize}</span>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {selectedCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileCode className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-2">No elements selected</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Select elements from the left panel to generate your custom ADF template.
              </p>
            </div>
          ) : (
            <CodeBlock
              code={xmlOutput}
              language="xml"
              showLineNumbers
            />
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
